// ── ActAsConfirmDialog (P12.2b) ──
// Typed-CONFIRM + reason gate to escalate from observe → act-as.
// SuperAdmin-only; rendered from ObserverBanner.

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ACT_AS_CONFIRM_PHRASE,
  ACT_AS_REASON_MIN,
} from "@/data/observerMode/actAsSession";

interface Props {
  open: boolean;
  observedLabel: string;
  onCancel: () => void;
  onConfirm: (reason: string) => boolean;
}

export function ActAsConfirmDialog({ open, observedLabel, onCancel, onConfirm }: Props) {
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setConfirm("");
    setReason("");
    setErr(null);
  };

  const handleClose = () => {
    reset();
    onCancel();
  };

  const handleConfirm = () => {
    if (confirm.trim() !== ACT_AS_CONFIRM_PHRASE) {
      setErr(`Type ${ACT_AS_CONFIRM_PHRASE} exactly to proceed.`);
      return;
    }
    if (reason.trim().length < ACT_AS_REASON_MIN) {
      setErr(`Reason must be at least ${ACT_AS_REASON_MIN} characters.`);
      return;
    }
    const ok = onConfirm(reason);
    if (ok) {
      reset();
    } else {
      setErr("Could not start Act-As session.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent data-testid="act-as-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Act as {observedLabel}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Pre-beta QA only. Writes will be tagged{" "}
            <code className="text-[10px]">actor:super_admin_pilot</code> and
            excluded from real pilot metrics. Window auto-expires in 10 minutes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Type {ACT_AS_CONFIRM_PHRASE} to proceed
            </label>
            <Input
              data-testid="act-as-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={ACT_AS_CONFIRM_PHRASE}
              className="mt-1"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Reason (≥ {ACT_AS_REASON_MIN} chars)
            </label>
            <Textarea
              data-testid="act-as-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Verifying offer-accept flow on staging contractor."
              rows={3}
              className="mt-1"
            />
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            data-testid="act-as-submit"
            variant="destructive"
            onClick={handleConfirm}
          >
            Begin Act-As
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
